import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CreditCard,
  Loader2,
  Minus,
  Plus,
  Users,
  Tag,
  AlertCircle,
  CheckCircle2,
  Package,
  Clock,
} from 'lucide-react';
import { useUserProfile } from '../context/UserProfileContext';
import { ConfirmModal } from '../components/admin/ConfirmModal';
import { api } from '../lib/api';
import {
  type AvailablePlan,
  type PlanPrice,
  formatCents,
  intervalLabel,
  intervalLabelFull,
  calculateTieredTotal,
  type CouponDetails,
  describeCouponDiscount,
  describeCouponDuration,
} from '../lib/billing';

// ── Page ───────────────────────────────────────────────────────────────────

export function ChoosePlanPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setSubscriptionSkipped } = useUserProfile();

  // ?plan=price_xxx from marketing site deep-link
  const preselectedPriceId = useMemo(() => searchParams.get('plan'), [searchParams]);

  const [plans, setPlans] = useState<AvailablePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [seats, setSeats] = useState(1);
  const [selectedInterval, setSelectedInterval] = useState<'month' | 'year'>('month');
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponSaving, setCouponSaving] = useState(false);
  const [couponResult, setCouponResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [appliedCoupon, setAppliedCoupon] = useState<CouponDetails | null>(null);

  useEffect(() => {
    api
      .get<{ success: boolean; data: AvailablePlan[] }>('/billing/available-plans')
      .then((res) => setPlans(res.data))
      .catch((err) => { console.error('[ChoosePlanPage] Failed to load plans:', err); })
      .finally(() => setLoading(false));
  }, [preselectedPriceId]);

  // Derived state
  const allPrices = plans.flatMap((p) => p.prices);
  const availableIntervals = [...new Set(allPrices.map((p) => p.interval))];

  const selectedPlan = plans.find((p) => p.id === selectedProductId) ?? plans[0] ?? null;
  const selectedPlanPrices = selectedPlan?.prices.filter((p) => p.interval === selectedInterval) ?? [];
  const selectedPrice = selectedPlanPrices[0] ?? null;
  const isFlatRate = !!(selectedPrice?.isFlatRate);
  const isFree = selectedPrice?.unitAmount === 0;

  const getEstimatedTotal = (price: PlanPrice | null, qty: number): number | null => {
    if (!price) return null;
    if (price.billingScheme === 'tiered' && price.tiers) {
      return calculateTieredTotal(price.tiers, qty, price.tiersMode) / 100;
    }
    if (price.unitAmount !== null) {
      return (price.isFlatRate ? 1 : qty) * price.unitAmount / 100;
    }
    return null;
  };

  const estimatedTotal = getEstimatedTotal(selectedPrice, seats);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;

    setCouponSaving(true);
    setCouponResult(null);

    try {
      const res = await api.post<{ success: boolean; data: CouponDetails }>(
        '/billing/validate-coupon',
        { code: couponCode.trim() },
      );

      const d = res.data;
      setCouponResult({
        ok: true,
        message: `Coupon "${d.couponName}" accepted: ${describeCouponDiscount(d)} ${describeCouponDuration(d)}.`,
      });
      setAppliedCoupon(d);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Invalid coupon.';
      setCouponResult({ ok: false, message });
      setAppliedCoupon(null);
    } finally {
      setCouponSaving(false);
    }
  };

  const handleStartCheckout = () => {
    if (!selectedPrice) {
      setError('No pricing available. Contact your IOMI representative.');
      return;
    }

    navigate('/checkout', {
      state: {
        priceId: selectedPrice.id,
        seats: isFlatRate ? 1 : seats,
        ...(appliedCoupon ? { couponCode: appliedCoupon.code } : {}),
        returnTo: '/',
      },
    });
  };

  const handleSkip = () => {
    setSubscriptionSkipped(true);
    navigate('/setup');
  };

  return (
    <div className="min-h-screen bg-mist flex flex-col items-center px-4 py-8 md:py-16">
      <div className="w-full max-w-md">
        {/* Welcome header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-plum/10 flex items-center justify-center mx-auto mb-4">
            <CreditCard size={28} className="text-plum" />
          </div>
          <h1 className="font-display text-h1 text-midnight mb-2">Choose Your Plan</h1>
          <p className="text-small text-slate leading-relaxed">
            Welcome to IOMI! Select a plan to get started.
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-plum" />
          </div>
        )}

        {!loading && (
          <>
            {/* ── Plan Selection (multiple products) ─────────────────── */}
            {plans.length > 1 && (
              <section className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Package size={18} className="text-plum" />
                  <h2 className="font-display text-h3 text-midnight">Select a Plan</h2>
                </div>

                <div className="space-y-2">
                  {plans.map((plan) => {
                    const monthlyPrice = plan.prices.find((p) => p.interval === 'month');
                    const annualPrice = plan.prices.find((p) => p.interval === 'year');
                    const displayPrice = monthlyPrice ?? annualPrice ?? plan.prices[0];
                    if (!displayPrice) return null;

                    const isSelected = (selectedProductId ?? plans[0]?.id) === plan.id;
                    const planIsFlatRate = displayPrice.isFlatRate;
                    const planIsFree = displayPrice.unitAmount === 0;
                    const altInterval = selectedInterval === 'month' ? 'year' : 'month';
                    const altPrice = plan.prices.find((p) => p.interval === altInterval);

                    return (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => setSelectedProductId(plan.id)}
                        className={`w-full text-left rounded-lg border-2 p-3 transition-colors
                                    ${isSelected
                                      ? 'border-plum bg-plum/5'
                                      : 'border-stone hover:border-plum/40 bg-white'}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-display text-[15px] font-semibold text-midnight">
                                {plan.name}
                              </p>
                              {planIsFree && (
                                <span className="text-[11px] font-semibold uppercase text-green-700 bg-green-50 border border-green-200 rounded-full px-1.5 py-0.5">
                                  Free
                                </span>
                              )}
                            </div>
                            {plan.description && (
                              <p className="text-[12px] text-slate mt-0.5 leading-snug">{plan.description}</p>
                            )}
                            {plan.trialDays && plan.trialDays > 0 && !planIsFree && (
                              <p className="text-[11px] text-plum font-medium mt-1 flex items-center gap-1">
                                <Clock size={11} className="shrink-0" />
                                {plan.trialDays}-day free trial
                              </p>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            {planIsFree ? (
                              <p className="font-mono text-[18px] font-medium text-green-700">$0</p>
                            ) : planIsFlatRate ? (
                              <>
                                <p className="font-mono text-[18px] font-medium text-midnight">
                                  {displayPrice.unitAmount !== null ? formatCents(displayPrice.unitAmount) : '—'}
                                </p>
                                <p className="text-[11px] text-slate">/{intervalLabel(displayPrice.interval)}</p>
                              </>
                            ) : displayPrice.billingScheme === 'tiered' && displayPrice.tiers ? (
                              <>
                                <p className="text-[12px] text-slate">
                                  From {formatCents(displayPrice.tiers[0].unitAmount)}
                                </p>
                                <p className="text-[11px] text-slate">/user/{intervalLabel(displayPrice.interval)}</p>
                              </>
                            ) : (
                              <>
                                <p className="font-mono text-[18px] font-medium text-midnight">
                                  {displayPrice.unitAmount !== null ? formatCents(displayPrice.unitAmount) : '—'}
                                </p>
                                <p className="text-[11px] text-slate">/user/{intervalLabel(displayPrice.interval)}</p>
                              </>
                            )}
                            {altPrice && !planIsFree && (
                              <p className="text-[10px] text-slate/70 mt-0.5">
                                or {altPrice.unitAmount !== null ? formatCents(altPrice.unitAmount) : '—'}/{intervalLabel(altInterval)}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* ── Single plan header (when only one product) ─────────── */}
            {plans.length === 1 && selectedPlan && (
              <section className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm mb-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-display text-[17px] font-semibold text-midnight">
                        {selectedPlan.name}
                      </p>
                      {isFree && (
                        <span className="text-[11px] font-semibold uppercase text-green-700 bg-green-50 border border-green-200 rounded-full px-1.5 py-0.5">
                          Free
                        </span>
                      )}
                    </div>
                    {selectedPlan.description && (
                      <p className="text-[13px] text-slate mt-1 leading-snug">{selectedPlan.description}</p>
                    )}
                    {selectedPlan.trialDays && selectedPlan.trialDays > 0 && !isFree && (
                      <p className="text-[13px] text-plum font-medium mt-1.5 flex items-center gap-1">
                        <Clock size={13} className="shrink-0" />
                        {selectedPlan.trialDays}-day free trial
                      </p>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* ── Billing Period ─────────────────────────────────────── */}
            {!isFree && availableIntervals.length > 1 && (
              <section className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard size={18} className="text-plum" />
                  <h2 className="font-display text-h3 text-midnight">Billing Period</h2>
                </div>
                <div className="flex gap-2">
                  {availableIntervals.includes('month') && (
                    <button
                      type="button"
                      onClick={() => setSelectedInterval('month')}
                      className={`flex-1 py-3 rounded-lg text-small font-medium border transition-colors
                                  ${selectedInterval === 'month'
                                    ? 'bg-plum text-white border-plum'
                                    : 'bg-white text-midnight border-stone hover:border-plum/40'}`}
                    >
                      Monthly
                    </button>
                  )}
                  {availableIntervals.includes('year') && (
                    <button
                      type="button"
                      onClick={() => setSelectedInterval('year')}
                      className={`flex-1 py-3 rounded-lg text-small font-medium border transition-colors
                                  ${selectedInterval === 'year'
                                    ? 'bg-plum text-white border-plum'
                                    : 'bg-white text-midnight border-stone hover:border-plum/40'}`}
                    >
                      Annual
                    </button>
                  )}
                </div>

                {selectedPrice && !isFlatRate && (
                  <div className="mt-3 bg-mist rounded-lg px-3 py-2">
                    {selectedPrice.billingScheme === 'tiered' && selectedPrice.tiers ? (
                      <div>
                        <p className="text-[12px] text-slate uppercase tracking-wider font-semibold mb-1">
                          {selectedPrice.tiersMode === 'volume' ? 'Volume' : 'Graduated'} pricing
                        </p>
                        {selectedPrice.tiers.map((tier, i) => (
                          <div key={i} className="text-[13px] text-midnight flex items-baseline gap-1">
                            <span className="font-mono text-plum font-medium">{formatCents(tier.unitAmount)}</span>
                            <span className="text-slate">/user/{intervalLabel(selectedInterval)}</span>
                            <span className="text-slate ml-1">
                              {tier.upTo === null
                                ? `(above ${i > 0 ? selectedPrice.tiers![i - 1].upTo : 0} users)`
                                : i === 0
                                  ? `(first ${tier.upTo} users)`
                                  : `(${(selectedPrice.tiers![i - 1]?.upTo ?? 0) + 1}–${tier.upTo} users)`}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className="text-small text-slate">Price per user</span>
                        <span className="font-mono text-body font-medium text-midnight">
                          {selectedPrice.unitAmount !== null ? formatCents(selectedPrice.unitAmount) : '—'}
                          /{intervalLabel(selectedInterval)}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {selectedPrice && isFlatRate && (
                  <div className="mt-3 bg-mist rounded-lg px-3 py-2 flex items-center justify-between">
                    <span className="text-small text-slate">
                      {intervalLabelFull(selectedInterval)} flat rate
                    </span>
                    <span className="font-mono text-body font-medium text-midnight">
                      {selectedPrice.unitAmount !== null ? formatCents(selectedPrice.unitAmount) : '—'}
                      /{intervalLabel(selectedInterval)}
                    </span>
                  </div>
                )}
              </section>
            )}

            {/* ── Seats (only for seat-based paid plans) ──────────────── */}
            {!isFlatRate && !isFree && (
              <section className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm mb-4">
                <div className="flex items-center gap-2 mb-4">
                  <Users size={18} className="text-plum" />
                  <h2 className="font-display text-h3 text-midnight">Users</h2>
                </div>

                <p className="text-small text-slate mb-4">
                  Each user is a license for one team member. You can add more users later.
                </p>

                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => setSeats((s) => Math.max(1, s - 1))}
                    disabled={seats <= 1}
                    className="w-11 h-11 flex items-center justify-center rounded-lg border border-stone
                               hover:border-plum/40 active:bg-mist disabled:opacity-40 disabled:cursor-not-allowed
                               transition-colors"
                  >
                    <Minus size={18} className="text-midnight" />
                  </button>

                  <div className="flex-1 text-center">
                    <p className="font-mono text-[32px] font-medium text-midnight leading-none">
                      {seats}
                    </p>
                    <p className="text-[13px] text-slate mt-1">
                      {seats === 1 ? 'user' : 'users'}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setSeats((s) => s + 1)}
                    className="w-11 h-11 flex items-center justify-center rounded-lg border border-stone
                               hover:border-plum/40 active:bg-mist
                               transition-colors"
                  >
                    <Plus size={18} className="text-midnight" />
                  </button>
                </div>
              </section>
            )}

            {/* ── Estimated Total (all paid plans) ─────────────────────── */}
            {!isFree && estimatedTotal !== null && (
              <div className="mb-4">
                <div className="bg-white border border-stone rounded-xl shadow-brand-sm px-4 py-3 flex items-center justify-between">
                  <span className="text-small text-slate">
                    {isFlatRate ? 'Flat rate' : `Estimated ${selectedInterval === 'year' ? 'annual' : 'monthly'} total`}
                  </span>
                  <span className="font-mono text-body font-medium text-midnight">
                    ${estimatedTotal.toFixed(2)}/{intervalLabel(selectedInterval)}
                  </span>
                </div>

                {appliedCoupon && (
                  <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 mt-1 flex items-center justify-between">
                    <span className="text-small text-green-700 flex items-center gap-1.5">
                      <Tag size={14} className="shrink-0" />
                      Coupon &ldquo;{appliedCoupon.couponName}&rdquo; applied {describeCouponDuration(appliedCoupon)}
                    </span>
                    <span className="font-mono text-small font-medium text-green-700">
                      {appliedCoupon.percentOff
                        ? `-${appliedCoupon.percentOff}%`
                        : appliedCoupon.amountOff
                          ? `-${formatCents(appliedCoupon.amountOff)}`
                          : ''}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* ── Coupon Code ─────────────────────────────────────────── */}
            {!isFree && (
              <section className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Tag size={16} className="text-plum" />
                  <h2 className="font-display text-h3 text-midnight">Coupon Code</h2>
                </div>

                <p className="text-small text-slate mb-3">
                  Have a coupon or promo code? Enter it below to apply a discount.
                </p>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter code"
                    className="flex-1 min-w-0 px-3 py-2.5 rounded-lg border border-stone
                               bg-white text-small text-midnight font-mono placeholder:text-slate
                               focus:outline-none focus:ring-2 focus:ring-plum/40 focus:border-plum
                               transition-colors"
                  />
                  <button
                    type="button"
                    onClick={handleApplyCoupon}
                    disabled={couponSaving || !couponCode.trim()}
                    className="btn-primary px-5 shrink-0"
                  >
                    {couponSaving ? 'Applying…' : 'Apply'}
                  </button>
                </div>

                {couponResult && (
                  <div
                    className={`mt-3 flex items-start gap-2 text-small ${
                      couponResult.ok ? 'text-green-600' : 'text-amber-600'
                    }`}
                  >
                    {couponResult.ok ? (
                      <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle size={16} className="shrink-0 mt-0.5" />
                    )}
                    <span>{couponResult.message}</span>
                  </div>
                )}
              </section>
            )}

            {/* ── No pricing available ────────────────────────────────── */}
            {plans.length === 0 && !loading && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
                <p className="text-[13px] font-semibold text-amber-800 mb-1">
                  No pricing available
                </p>
                <p className="text-[12px] text-amber-700">
                  Contact your IOMI representative to set up pricing for your organization.
                </p>
              </div>
            )}

            {/* ── Error ───────────────────────────────────────────────── */}
            {error && (
              <div className="flex items-start gap-2 text-small text-red-600 mb-3">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* ── Subscribe / Start Free Plan button ──────────────────── */}
            {plans.length > 0 && (
              <button
                type="button"
                onClick={handleStartCheckout}
                disabled={!selectedPrice}
                className="btn btn-primary w-full flex items-center justify-center gap-2 mb-1"
              >
                {isFree ? 'Start Free Plan' : 'Subscribe Now'}
              </button>
            )}

            {/* Trial info */}
            {selectedPlan?.trialDays && selectedPlan.trialDays > 0 && !isFree && (
              <p className="text-[12px] text-plum text-center mb-2 flex items-center justify-center gap-1">
                <Clock size={12} />
                Includes a {selectedPlan.trialDays}-day free trial
              </p>
            )}

            {/* Skip for now */}
            <button
              type="button"
              onClick={() => setShowSkipConfirm(true)}
              className="w-full py-3 text-small text-slate hover:text-midnight transition-colors text-center"
            >
              Skip for now
            </button>

            <ConfirmModal
              isOpen={showSkipConfirm}
              title="Skip Subscription?"
              message="Without an active subscription, you won't be able to create or view quotes. You can subscribe at any time from the Billing page."
              confirmLabel="Skip"
              onConfirm={handleSkip}
              onClose={() => setShowSkipConfirm(false)}
            />
          </>
        )}
      </div>
    </div>
  );
}
